import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, FlatList, Alert, ScrollView } from 'react-native';
import { Card, Button, TextInput, ActivityIndicator, FAB, IconButton, RadioButton, Switch } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';

const ConfigPenalizacionesScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRegla, setEditingRegla] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    nombre: '',
    tipo: 'penalizacion',
    motivo: '',
    monto: '',
    porcentaje: '',
    aplica_a: 'repartidor',
    automatico: false,
  });

  useEffect(() => {
    loadData();
    const unsubscribe = navigation.addListener('focus', loadData);
    return unsubscribe;
  }, [navigation]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await api.getPenalizacionReglas();
      setReglas(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ nombre: '', tipo: 'penalizacion', motivo: '', monto: '', porcentaje: '', aplica_a: 'repartidor', automatico: false });
    setEditingRegla(null);
    setShowForm(false);
  };

  const handleEdit = (regla) => {
    setForm({
      nombre: regla.nombre,
      tipo: regla.tipo,
      motivo: regla.motivo,
      monto: regla.monto.toString(),
      porcentaje: regla.porcentaje.toString(),
      aplica_a: regla.aplica_a,
      automatico: !!regla.automatico,
    });
    setEditingRegla(regla);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim() || !form.motivo.trim()) {
      Alert.alert('Error', 'Nombre y motivo son requeridos');
      return;
    }

    try {
      setSaving(true);
      const data = {
        nombre: form.nombre,
        tipo: form.tipo,
        motivo: form.motivo,
        monto: parseFloat(form.monto) || 0,
        porcentaje: parseFloat(form.porcentaje) || 0,
        aplica_a: form.aplica_a,
        automatico: form.automatico ? 1 : 0,
        activo: 1,
      };

      if (editingRegla) {
        await api.updatePenalizacionRegla(editingRegla.id, data);
      } else {
        await api.createPenalizacionRegla(data);
      }

      resetForm();
      loadData();
    } catch (error) {
      Alert.alert('Error', error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (regla) => {
    Alert.alert('Desactivar Regla', `¿Desactivar "${regla.nombre}"?`, [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Desactivar', style: 'destructive', onPress: async () => {
          try {
            await api.deletePenalizacionRegla(regla.id);
            loadData();
          } catch (error) {
            Alert.alert('Error', error.message);
          }
        }
      }
    ]);
  };

  const renderRegla = ({ item }) => (
    <Card style={[styles.card, !item.activo && styles.cardInactive]}>
      <Card.Content>
        <View style={styles.reglaHeader}>
          <View style={styles.reglaInfo}>
            <Ionicons
              name={item.tipo === 'bono' ? 'add-circle' : 'remove-circle'}
              size={18}
              color={item.tipo === 'bono' ? '#28A745' : '#DC3545'}
            />
            <Text style={styles.reglaNombre}>{item.nombre}</Text>
          </View>
          <View style={styles.reglaActions}>
            <IconButton icon="pencil" size={18} onPress={() => handleEdit(item)} />
            {item.activo ? <IconButton icon="delete" size={18} iconColor="#DC3545" onPress={() => handleDelete(item)} /> : null}
          </View>
        </View>
        <Text style={styles.reglaDetalle}>Motivo: {item.motivo}</Text>
        <Text style={styles.reglaDetalle}>Aplica a: {item.aplica_a}</Text>
        <View style={styles.reglaValores}>
          {parseFloat(item.monto) > 0 && <Text style={styles.reglaValor}>L. {parseFloat(item.monto).toFixed(2)}</Text>}
          {parseFloat(item.porcentaje) > 0 && <Text style={styles.reglaValor}>{item.porcentaje}%</Text>}
          {item.automatico ? <Text style={styles.autoTag}>AUTO</Text> : null}
        </View>
        {!item.activo && <Text style={styles.inactiveText}>INACTIVA</Text>}
      </Card.Content>
    </Card>
  );

  if (loading) {
    return <View style={styles.loadingContainer}><ActivityIndicator size="large" color="#007bff" /></View>;
  }

  return (
    <View style={styles.container}>
      {showForm && (
        <ScrollView style={styles.formContainer}>
          <Card style={styles.card}>
            <Card.Title title={editingRegla ? 'Editar Regla' : 'Nueva Regla'} />
            <Card.Content>
              <TextInput label="Nombre*" value={form.nombre} onChangeText={v => setForm({ ...form, nombre: v })} mode="outlined" style={styles.input} />

              <Text style={styles.fieldLabel}>Tipo:</Text>
              <RadioButton.Group onValueChange={v => setForm({ ...form, tipo: v })} value={form.tipo}>
                <RadioButton.Item label="Penalización" value="penalizacion" />
                <RadioButton.Item label="Bono" value="bono" />
              </RadioButton.Group>

              <TextInput label="Motivo clave*" value={form.motivo} onChangeText={v => setForm({ ...form, motivo: v })} mode="outlined" style={styles.input} placeholder="ej: entrega_incompleta, entrega_completa, daño" />

              <TextInput label="Monto fijo (L.)" value={form.monto} onChangeText={v => setForm({ ...form, monto: v })} mode="outlined" keyboardType="decimal-pad" style={styles.input} />
              <TextInput label="Porcentaje (%)" value={form.porcentaje} onChangeText={v => setForm({ ...form, porcentaje: v })} mode="outlined" keyboardType="decimal-pad" style={styles.input} />

              <Text style={styles.fieldLabel}>Aplica a:</Text>
              <RadioButton.Group onValueChange={v => setForm({ ...form, aplica_a: v })} value={form.aplica_a}>
                <RadioButton.Item label="Repartidor" value="repartidor" />
                <RadioButton.Item label="Vendedor" value="vendedor" />
                <RadioButton.Item label="Bodega" value="bodega" />
                <RadioButton.Item label="Todos" value="todos" />
              </RadioButton.Group>

              <View style={styles.switchRow}>
                <Text style={styles.fieldLabel}>Automático:</Text>
                <Switch value={form.automatico} onValueChange={v => setForm({ ...form, automatico: v })} />
              </View>

              <View style={styles.formButtons}>
                <Button mode="outlined" onPress={resetForm} style={styles.formButton}>Cancelar</Button>
                <Button mode="contained" onPress={handleSave} loading={saving} disabled={saving} style={styles.formButton}>
                  {editingRegla ? 'Actualizar' : 'Crear'}
                </Button>
              </View>
            </Card.Content>
          </Card>
        </ScrollView>
      )}

      {!showForm && (
        <>
          <FlatList
            data={reglas}
            keyExtractor={(item) => item.id.toString()}
            renderItem={renderRegla}
            ListEmptyComponent={<Text style={styles.emptyText}>No hay reglas configuradas</Text>}
            contentContainerStyle={reglas.length === 0 ? styles.emptyContainer : { paddingBottom: 80 }}
          />
          <FAB style={[styles.fab, { marginBottom: insets.bottom }]} icon="plus" onPress={() => setShowForm(true)} />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  formContainer: { flex: 1 },
  card: { margin: 8, elevation: 2 },
  cardInactive: { opacity: 0.5 },
  input: { marginBottom: 8 },
  fieldLabel: { fontSize: 14, fontWeight: '600', color: '#333', marginTop: 8, marginBottom: 4 },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginVertical: 8 },
  formButtons: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 12, gap: 8 },
  formButton: { marginLeft: 8 },
  reglaHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  reglaInfo: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  reglaNombre: { fontSize: 15, fontWeight: 'bold', color: '#333', marginLeft: 8 },
  reglaActions: { flexDirection: 'row' },
  reglaDetalle: { fontSize: 13, color: '#666', marginTop: 2, marginLeft: 26 },
  reglaValores: { flexDirection: 'row', marginTop: 6, marginLeft: 26, gap: 8, alignItems: 'center' },
  reglaValor: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  autoTag: { backgroundColor: '#007bff', color: '#fff', fontSize: 10, fontWeight: '700', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8 },
  inactiveText: { color: '#DC3545', fontWeight: 'bold', marginTop: 4, fontSize: 12, marginLeft: 26 },
  emptyText: { textAlign: 'center', color: '#999', fontSize: 16, marginTop: 40 },
  emptyContainer: { flex: 1, justifyContent: 'center' },
  fab: { position: 'absolute', margin: 16, right: 0, bottom: 0, backgroundColor: '#007bff' },
});

export default ConfigPenalizacionesScreen;
